import { RefreshCw, Route } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../components/Badge';
import { Modal } from '../components/Modal';
import type { CatalogModel, ModelCatalog, ModelRoute } from '../hooks/useAdminData';
import { maxTemperatureFor, parseMaxTokens, parseTemperature } from '../lib/modelRouteParams';
import {
  useBulkUpsertModelRoutes,
  useModelCatalog,
  useModelRoutes,
  useRefreshModelCatalog,
  useUpsertModelRoute,
} from '../hooks/useAdminData';

function formatTaskType(value: string) {
  return value.replaceAll('_', ' ');
}

function formatPrice(value: string | null) {
  if (value === null) return '—';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `$${parsed}` : '—';
}

function formatSyncedAt(value: string | null) {
  if (!value) return 'never';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'never' : parsed.toLocaleString();
}

const TIERS = ['fast', 'balanced', 'premium'];

function formatPlans(plans: string[]) {
  return plans.map((plan) => plan.charAt(0).toUpperCase() + plan.slice(1)).join(', ');
}

function routeKey(route: { taskType: string; tier: string }) {
  return `${route.taskType}:${route.tier}`;
}

function errorMessage(error: unknown) {
  const apiMessage = (error as { response?: { data?: { message?: unknown } } } | null)?.response
    ?.data?.message;
  if (Array.isArray(apiMessage)) return apiMessage.join(', ');
  if (typeof apiMessage === 'string') return apiMessage;
  return error instanceof Error ? error.message : 'Could not save the routes.';
}

interface EditState {
  taskType: string;
  tier: string;
  allowedProviders: string[];
  provider: string;
  model: string;
  reason: string;
  isActive: boolean;
  temperature: string;
  maxTokens: string;
}

function toEditState(route: ModelRoute): EditState {
  const providerAllowed = !!route.provider && route.allowedProviders.includes(route.provider);

  return {
    taskType: route.taskType,
    tier: route.tier,
    allowedProviders: route.allowedProviders,
    provider: providerAllowed ? (route.provider as string) : (route.allowedProviders[0] ?? ''),
    model: providerAllowed ? (route.model ?? '') : '',
    reason: route.reason ?? '',
    isActive: route.isConfigured ? route.isActive : true,
    temperature: route.temperature === null ? '' : String(route.temperature),
    maxTokens: route.maxTokens === null ? '' : String(route.maxTokens),
  };
}

function formatParam(value: number | null) {
  return value === null ? 'default' : String(value);
}

function isUsable(route: ModelRoute) {
  return route.isConfigured && route.isActive;
}

function sharedProviders(routes: ModelRoute[]) {
  const [first, ...rest] = routes;
  if (!first) return [];
  return first.allowedProviders.filter((provider) =>
    rest.every((route) => route.allowedProviders.includes(provider)),
  );
}

function MissingRoutesBanner({ missing, total }: { missing: number; total: number }) {
  if (missing === 0) return null;

  return (
    <section
      aria-label="Missing model routes"
      className="rounded border border-warning/40 bg-warning/10 px-4 py-3 text-sm"
    >
      <p aria-live="polite" className="font-medium text-text">
        {missing} of {total} {total === 1 ? 'route' : 'routes'} {missing === 1 ? 'is' : 'are'} not
        configured — agents using them will fail.
      </p>
      <p className="mt-1 text-text-secondary">
        Edit each route marked Not configured or Inactive and save an active model.
      </p>
    </section>
  );
}

/**
 * Models the given provider can serve, with the currently stored model added
 * back if the catalog no longer lists it.
 *
 * Without that fallback a retired model — or any model belonging to a provider
 * whose sync is failing — would drop out of the select, and the browser would
 * quietly present a different option as selected. Saving would then change the
 * route to a model the admin never picked.
 */
function optionsFor(
  catalog: ModelCatalog | undefined,
  provider: string,
  currentModel: string,
): CatalogModel[] {
  const models = catalog?.providers.find((p) => p.provider === provider)?.models ?? [];
  if (!currentModel || models.some((m) => m.id === currentModel)) return models;

  return [
    {
      id: currentModel,
      displayName: `${currentModel} (not in catalog)`,
      inputPricePerMTok: null,
      outputPricePerMTok: null,
      source: 'static',
    },
    ...models,
  ];
}

interface ModelPrice {
  input: number;
  output: number;
}

function priceOf(
  catalog: ModelCatalog | undefined,
  provider: string,
  modelId: string,
): ModelPrice | null {
  const entry = catalog?.providers
    .find((p) => p.provider === provider)
    ?.models.find((m) => m.id === modelId);
  if (!entry || entry.inputPricePerMTok === null || entry.outputPricePerMTok === null) return null;
  return { input: entry.inputPricePerMTok, output: entry.outputPricePerMTok };
}

function PriceNote({ modelId, price }: { modelId: string; price: ModelPrice | null }) {
  if (!modelId) return null;

  return (
    <>
      {price ? (
        <p className="text-sm text-text-secondary">
          Price per MTok (in / out):{' '}
          <span data-testid="model-price" className="font-medium text-text">
            ${price.input} / ${price.output}
          </span>
        </p>
      ) : (
        <div
          role="alert"
          className="rounded border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-text-secondary"
        >
          This model has no price, so it cannot be used for a route. Add its price in Settings →
          Models first.
        </div>
      )}
      <p className="text-xs text-text-muted">
        Prices belong to the model and are managed in Settings → Models.
      </p>
    </>
  );
}

interface ModelPickerProps {
  providers: ModelCatalog['providers'];
  provider: string;
  model: string;
  options: CatalogModel[];
  loading: boolean;
  onProvider: (provider: string) => void;
  onModel: (model: string) => void;
}

function ModelPicker({
  providers,
  provider,
  model,
  options,
  loading,
  onProvider,
  onModel,
}: ModelPickerProps) {
  return (
    <>
      <label className="block">
        <span className="text-text-secondary">Provider</span>
        <select
          aria-label="Provider"
          className="mt-1 w-full rounded border border-border bg-surface px-3 py-2 text-text"
          value={provider}
          onChange={(e) => onProvider(e.target.value)}
        >
          {providers.map((p) => (
            <option key={p.provider} value={p.provider}>
              {p.provider}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-text-secondary">Model</span>
        <select
          aria-label="Model"
          className="mt-1 w-full rounded border border-border bg-surface px-3 py-2 text-text font-mono"
          value={model}
          onChange={(e) => onModel(e.target.value)}
        >
          <option value="">{loading ? 'Loading models…' : 'Select a model…'}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.id}
              {option.source === 'live' ? ' · new' : ''}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}

interface ParamFieldProps {
  id: string;
  label: string;
  value: string;
  hint: string;
  error: string | null;
  onChange: (value: string) => void;
}

function ParamField({ id, label, value, hint, error, onChange }: ParamFieldProps) {
  const describedBy = error ? `${id}-error` : `${id}-hint`;

  return (
    <div>
      <label htmlFor={id} className="text-text-secondary">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        placeholder="Default"
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={describedBy}
        className={`mt-1 w-full rounded border bg-surface px-3 py-2 text-text font-mono ${
          error ? 'border-danger' : 'border-border'
        }`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs font-medium text-danger">
          {error}
        </p>
      ) : (
        <p id={`${id}-hint`} className="mt-1 text-xs text-text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}

interface BulkState {
  provider: string;
  model: string;
  reason: string;
}

export function ModelRoutes() {
  const { data: routes, isLoading } = useModelRoutes();
  const { data: catalog, isLoading: catalogLoading } = useModelCatalog();
  const upsert = useUpsertModelRoute();
  const bulkUpsert = useBulkUpsertModelRoutes();
  const refresh = useRefreshModelCatalog();
  const [edit, setEdit] = useState<EditState | null>(null);
  const [bulk, setBulk] = useState<BulkState | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const providers = catalog?.providers ?? [];
  const failedSyncs = providers.filter((p) => p.syncError);
  const modelOptions = edit ? optionsFor(catalog, edit.provider, edit.model) : [];
  const editPrice = edit ? priceOf(catalog, edit.provider, edit.model) : null;
  const bulkOptions = bulk ? optionsFor(catalog, bulk.provider, bulk.model) : [];
  const bulkPrice = bulk ? priceOf(catalog, bulk.provider, bulk.model) : null;
  const temperatureParam = edit ? parseTemperature(edit.temperature, edit.provider) : null;
  const maxTokensParam = edit ? parseMaxTokens(edit.maxTokens) : null;
  const paramsInvalid = !!(temperatureParam?.error || maxTokensParam?.error);

  const allRoutes = routes ?? [];
  const missingCount = allRoutes.filter((route) => !isUsable(route)).length;
  const selectedRoutes = allRoutes.filter((route) => selected.has(routeKey(route)));
  const editProviders = edit
    ? providers.filter((p) => edit.allowedProviders.includes(p.provider))
    : [];
  const bulkAllowed = sharedProviders(selectedRoutes);
  const bulkProviders = providers.filter((p) => bulkAllowed.includes(p.provider));
  const allSelected = allRoutes.length > 0 && selectedRoutes.length === allRoutes.length;
  const tiersInUse = TIERS.filter((tier) => allRoutes.some((route) => route.tier === tier));
  const plansByTier = tiersInUse.map((tier) => ({
    tier,
    plans: Array.from(
      new Set(allRoutes.filter((route) => route.tier === tier).flatMap((route) => route.plans)),
    ),
  }));

  function toggleRoute(route: ModelRoute) {
    const next = new Set(selected);
    const key = routeKey(route);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(allRoutes.map(routeKey)));
  }

  function selectTier(tier: string) {
    setSelected(new Set(allRoutes.filter((route) => route.tier === tier).map(routeKey)));
  }

  function openBulk() {
    bulkUpsert.reset();
    const preferred = selectedRoutes[0]?.provider;
    setBulk({
      provider:
        preferred && bulkAllowed.includes(preferred)
          ? preferred
          : (bulkProviders[0]?.provider ?? ''),
      model: '',
      reason: '',
    });
  }

  function saveBulk() {
    if (!bulk) return;
    bulkUpsert.mutate(
      {
        provider: bulk.provider,
        model: bulk.model,
        ...(bulk.reason ? { reason: bulk.reason } : {}),
        targets: selectedRoutes.map(({ taskType, tier }) => ({ taskType, tier })),
      },
      {
        onSuccess: () => {
          setBulk(null);
          setSelected(new Set());
        },
      },
    );
  }

  function selectProvider(provider: string) {
    if (!edit) return;
    setEdit({ ...edit, provider, model: '' });
  }

  function selectModel(modelId: string) {
    if (!edit) return;
    setEdit({
      ...edit,
      model: modelId,
      reason: modelId === edit.model ? edit.reason : '',
    });
  }

  function save() {
    if (!edit) return;
    upsert.mutate({
      taskType: edit.taskType,
      tier: edit.tier,
      data: {
        provider: edit.provider,
        model: edit.model,
        ...(edit.reason ? { reason: edit.reason } : {}),
        isActive: edit.isActive,
        temperature: temperatureParam?.value ?? null,
        maxTokens: maxTokensParam?.value ?? null,
      },
    });
    setEdit(null);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Route size={22} className="text-primary" />
          <div>
            <h1 className="text-2xl font-semibold">Model Routes</h1>
            <p className="text-sm text-text-muted mt-1">
              Which model serves each task type at each plan tier. There are no built-in models: a
              task type without an active route fails until one is configured here.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="flex shrink-0 items-center gap-2 rounded border border-border px-3 py-2 text-sm text-text-secondary hover:text-text disabled:opacity-50"
          disabled={refresh.isPending}
          onClick={() => refresh.mutate()}
        >
          <RefreshCw size={14} className={refresh.isPending ? 'animate-spin' : undefined} />
          {refresh.isPending ? 'Refreshing…' : 'Refresh models'}
        </button>
      </div>

      {isLoading ? null : <MissingRoutesBanner missing={missingCount} total={allRoutes.length} />}

      {refresh.isSuccess && !refresh.isPending ? (
        <p role="status" className="text-xs font-medium text-success">
          Model list refreshed.
        </p>
      ) : null}

      {providers.length > 0 ? (
        <p className="text-xs text-text-muted">
          Model list synced{' '}
          {providers.map((p, i) => (
            <span key={p.provider}>
              {i > 0 ? ' · ' : ''}
              {p.provider}: {formatSyncedAt(p.syncedAt)}
            </span>
          ))}
        </p>
      ) : null}

      {failedSyncs.map((p) => (
        <div
          key={p.provider}
          className="rounded border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-text-secondary"
        >
          Could not reach {p.provider} for its model list, so only the models compiled into the
          build are offered: {p.syncError}
        </div>
      ))}

      <section
        aria-label="How tiers work"
        className="rounded border border-border bg-surface px-4 py-3 text-sm text-text-secondary"
      >
        <h2 className="font-medium text-text">How tiers work</h2>
        <ul className="mt-1 list-disc space-y-1 pl-5">
          <li>
            The tier is chosen by the organization&apos;s subscription plan:{' '}
            {plansByTier.map(({ tier, plans }, i) => (
              <span key={tier}>
                {i > 0 ? ' · ' : ''}
                {tier}: {formatPlans(plans)}
              </span>
            ))}
          </li>
          <li>Support can pin one organization to a tier with an override in its settings.</li>
          <li>Low-confidence results escalate one tier up: fast → balanced → premium.</li>
        </ul>
      </section>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-text-secondary">Select:</span>
        {tiersInUse.map((tier) => (
          <button
            key={tier}
            type="button"
            className="rounded border border-border px-3 py-1 text-text-secondary hover:text-text"
            onClick={() => selectTier(tier)}
          >
            Select {tier} tier
          </button>
        ))}
        {selectedRoutes.length > 0 ? (
          <>
            <button
              type="button"
              className="rounded px-3 py-1 text-text-secondary hover:text-text"
              onClick={() => setSelected(new Set())}
            >
              Clear selection
            </button>
            <button
              type="button"
              className="rounded bg-primary px-3 py-1 font-medium text-white"
              onClick={openBulk}
            >
              Edit selected ({selectedRoutes.length})
            </button>
          </>
        ) : null}
      </div>

      {isLoading ? (
        <div className="text-text-secondary">Loading routes…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-text-secondary">
              <tr>
                <th className="px-4 py-2">
                  <input
                    type="checkbox"
                    aria-label="Select all routes"
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-2">Task</th>
                <th className="px-4 py-2">Tier</th>
                <th className="px-4 py-2">Provider</th>
                <th className="px-4 py-2">Model</th>
                <th className="px-4 py-2">In / Out per MTok</th>
                <th className="px-4 py-2">Temp / Max tokens</th>
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {routes?.map((route) => (
                <tr key={routeKey(route)} className="border-t border-border hover:bg-surface">
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${route.taskType} / ${route.tier}`}
                      checked={selected.has(routeKey(route))}
                      onChange={() => toggleRoute(route)}
                    />
                  </td>
                  <td className="px-4 py-2 text-text">{formatTaskType(route.taskType)}</td>
                  <td className="px-4 py-2 text-text-secondary">
                    {route.tier}
                    {route.plans.length > 0 ? (
                      <span className="block text-xs text-text-muted">
                        {formatPlans(route.plans)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-text-secondary">{route.provider ?? '—'}</td>
                  <td className="px-4 py-2 font-mono text-text">{route.model ?? '—'}</td>
                  <td className="px-4 py-2 text-text-secondary">
                    {route.inputPricePerMTok === null || route.outputPricePerMTok === null ? (
                      <Badge variant="warning">Unpriced</Badge>
                    ) : (
                      <>
                        {formatPrice(route.inputPricePerMTok)} /{' '}
                        {formatPrice(route.outputPricePerMTok)}
                      </>
                    )}
                  </td>
                  <td
                    data-testid="route-params"
                    className="px-4 py-2 font-mono text-text-secondary"
                  >
                    {formatParam(route.temperature)} / {formatParam(route.maxTokens)}
                  </td>
                  <td className="px-4 py-2">
                    {!route.isConfigured ? (
                      <Badge variant="warning">Not configured</Badge>
                    ) : route.isActive ? (
                      <Badge variant="success">v{route.version}</Badge>
                    ) : (
                      <Badge variant="default">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      aria-label={`Edit ${route.taskType} / ${route.tier}`}
                      className="text-primary hover:underline"
                      onClick={() => setEdit(toEditState(route))}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit ? `${formatTaskType(edit.taskType)} / ${edit.tier}` : 'Edit route'}
      >
        {edit ? (
          <div className="space-y-4 text-sm">
            <ModelPicker
              providers={editProviders}
              provider={edit.provider}
              model={edit.model}
              options={modelOptions}
              loading={catalogLoading}
              onProvider={selectProvider}
              onModel={selectModel}
            />

            <PriceNote modelId={edit.model} price={editPrice} />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ParamField
                id="route-temperature"
                label="Temperature"
                value={edit.temperature}
                hint={`0 to ${maxTemperatureFor(edit.provider)}, up to two decimals. Leave empty for the code default.`}
                error={temperatureParam?.error ?? null}
                onChange={(temperature) => setEdit({ ...edit, temperature })}
              />
              <ParamField
                id="route-max-tokens"
                label="Max tokens"
                value={edit.maxTokens}
                hint="Whole number, 1 to 64000. Leave empty for the code default."
                error={maxTokensParam?.error ?? null}
                onChange={(maxTokens) => setEdit({ ...edit, maxTokens })}
              />
            </div>

            <label className="block">
              <span className="text-text-secondary">Reason</span>
              <input
                aria-label="Reason"
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-2 text-text"
                value={edit.reason}
                onChange={(e) => setEdit({ ...edit, reason: e.target.value })}
              />
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label="Active"
                checked={edit.isActive}
                onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })}
              />
              <span className="text-text-secondary">
                Active — when off, calls on this route fail
              </span>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded px-4 py-2 text-text-secondary hover:text-text"
                onClick={() => setEdit(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-primary px-4 py-2 font-medium text-white disabled:opacity-50"
                disabled={!edit.model || !editPrice || paramsInvalid || upsert.isPending}
                onClick={save}
              >
                Save route
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal open={!!bulk} onClose={() => setBulk(null)} title="Edit selected routes">
        {bulk ? (
          <div className="space-y-4 text-sm">
            <p className="text-text-secondary">
              {selectedRoutes.length} {selectedRoutes.length === 1 ? 'route' : 'routes'} will change
              to the model picked below.
            </p>

            {bulkProviders.length === 0 ? (
              <div
                role="alert"
                className="rounded border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-text-secondary"
              >
                The selected task types have no provider in common. Select routes that can share one
                model.
              </div>
            ) : null}

            <ModelPicker
              providers={bulkProviders}
              provider={bulk.provider}
              model={bulk.model}
              options={bulkOptions}
              loading={catalogLoading}
              onProvider={(provider) => setBulk({ ...bulk, provider, model: '' })}
              onModel={(model) => setBulk({ ...bulk, model })}
            />

            <PriceNote modelId={bulk.model} price={bulkPrice} />

            <label className="block">
              <span className="text-text-secondary">Reason</span>
              <input
                aria-label="Reason"
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-2 text-text"
                value={bulk.reason}
                onChange={(e) => setBulk({ ...bulk, reason: e.target.value })}
              />
            </label>

            {bulkUpsert.isError ? (
              <div
                role="alert"
                className="rounded border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-text-secondary"
              >
                {errorMessage(bulkUpsert.error)}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded px-4 py-2 text-text-secondary hover:text-text"
                onClick={() => setBulk(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-primary px-4 py-2 font-medium text-white disabled:opacity-50"
                disabled={!bulk.model || !bulkPrice || bulkUpsert.isPending}
                onClick={saveBulk}
              >
                Apply to selected
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
